import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

type BffError = { message: string };

const translateError = async (
    response: Response,
    t: TFunction,
    resourceName?: string,
    fallbackMessage?: string,
) => {
    const _response = await response.json().catch(() => null);

    if ((_response as BffError | null)?.message) {
        return (_response as BffError).message;
    }

    if (fallbackMessage) {
        return fallbackMessage;
    }

    const status = response.status;
    switch (status) {
        case 401:
            return t("errors.error_notifications.401");
        case 403:
            return t("errors.error_notifications.403");
        case 404:
            return t("errors.error_notifications.404");
        case 504:
            return t("errors.error_notifications.504");
    }

    if (resourceName) {
        return t("errors.error_notifications.default_with_resource", {
            resourceName,
        });
    }

    return t("errors.error_notifications.default");
};

export const useErrorNotification = () => {
    const { t } = useTranslation();

    const showTextMessage = useCallback(
        (message?: string, resourceName?: string) => {
            const finalMessage =
                message ??
                (resourceName
                    ? t("errors.error_notifications.default_with_resource", {
                          resourceName,
                      })
                    : t("errors.error_notifications.default"));

            toast.error(finalMessage, {
                position: "top-right",
                hideProgressBar: true,
            });
        },
        [t],
    );

    const showFetchErrorMessage = useCallback(
        async (
            response: Response,
            resourceName?: string,
            fallbackMessage?: string,
        ) => {
            const message = await translateError(
                response,
                t,
                resourceName,
                fallbackMessage,
            );
            toast.error(message, {
                position: "top-right",
                hideProgressBar: true,
            });
        },
        [t],
    );

    return { showTextMessage, showFetchErrorMessage };
};
